import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ShoppingCart, Package, Globe, Briefcase, Droplet } from 'lucide-react';
import { addToCart, setCartItems } from '../../redux/cartSlice';
import { AuthContext } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';

const SECTION_COLORS = {
  "National Articles": "#10b981",
  "Premium & Imported": "#3b82f6",
  "Accessories": "#8b5cf6",
  "Fragrance & Grooming": "#f97316"
};

const SECTION_ICONS = {
  "National Articles": Package,
  "Premium & Imported": Globe,
  "Accessories": Briefcase,
  "Fragrance & Grooming": Droplet
};

const ShopProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const { user } = useContext(AuthContext);
  const cartItems = useSelector((state) => state.cart.cartItems) || [];

  if (!product) return null;

  const sectionColor = SECTION_COLORS[product.section] || "#f97316";
  const sectionColorGlow = sectionColor.startsWith("#") ? `${sectionColor}26` : "rgba(255, 255, 255, 0.15)";

  const cardStyle = {
    "--section-color": sectionColor,
    "--section-color-glow": sectionColorGlow,
  };

  const getProductImage = () => {
    if (product.images && product.images[0] && product.images[0].url) {
      return product.images[0].url;
    }
    if (product.imageUrl) {
      return product.imageUrl;
    }
    return "/placeholder_product.png";
  };

  const formattedMoq = () => {
    const count = product.moq || 1;
    if (product.moqType === 'set') {
      const pcsCount = count * (product.unitsPerSet || 1);
      return `MOQ: ${count} Sets (${pcsCount} Pcs)`;
    }
    return `MOQ: ${count} Pcs`;
  };

  const categoryName = product.category?.name || "General";

  const handleAddToCartClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check availability
    const isAvailable = product.isAvailable === true;
    if (!isAvailable) {
      alert("Product is currently unavailable.");
      return;
    }

    const isAlreadyInCart = cartItems.some((item) => item.productId === product._id);
    if (isAlreadyInCart) {
      alert("Product already in your cart!");
      return;
    }

    const isSet = product.moqType === 'set';
    const qty = isSet 
      ? (product.moq || 1) * (product.unitsPerSet || 1) 
      : (product.moq || 1);

    const imageUrls = product.images?.map((img) => 
      typeof img === 'string' ? img : img?.url
    ).filter(Boolean) || [];
    
    const primaryImage = imageUrls[0] || product.imageUrl || '/placeholder_product.png';

    if (user && user.token) {
      try {
        const res = await apiFetch('/api/cart', {
          method: 'POST',
          body: JSON.stringify({ productId: product._id, qty })
        });
        const data = await res.json();
        if (!res.ok) {
          alert(data.message || 'Failed to add product to cart');
          return;
        }
        dispatch(setCartItems(data));
        alert('Successfully added to your cart!');
      } catch (err) {
        console.error(err);
        alert('Failed to add product to cart');
      }
    } else {
      dispatch(
        addToCart({
          productId: product._id,
          name: product.name,
          price: product.price,
          image: primaryImage,
          imageUrl: primaryImage,
          images: imageUrls,
          qty: qty,
          section: product.section,
          category: categoryName,
          moqType: product.moqType || 'pcs',
          unitsPerSet: product.unitsPerSet || 1,
          isAvailable: product.isAvailable
        })
      );
      alert('Successfully added to your cart!');
    }
  };

  const IconComponent = SECTION_ICONS[product.section] || null;

  return (
    <Link to={`/product/${product._id}`} className="shop-product-card" style={cardStyle}>
      <div className="shop-product-card-image-wrapper">
        <img
          src={getProductImage()}
          alt={product.name}
          className="shop-product-card-image"
          loading="lazy"
        />
        <span className="shop-product-card-section-tag">
          {IconComponent && <IconComponent size={14} />}
        </span>
      </div>

      <div className="shop-product-card-info">
        <div className="shop-product-card-header">
          <span className="shop-product-card-category">{categoryName}</span>
          <div className="shop-product-card-section-pill">
            {IconComponent && <IconComponent size={12} className="pill-icon" />}
            <span>{product.section}</span>
          </div>
          <h3 className="shop-product-card-name" title={product.name}>{product.name}</h3>
        </div>

        <div className="shop-product-card-meta">
          <span className="shop-product-card-price">
            ₹{product.price}
            <span className="shop-product-card-unit"> /pc</span>
          </span>
          <span className="shop-product-card-moq">{formattedMoq()}</span>
        </div>

        <div className="shop-product-card-actions">
          <button 
            onClick={handleAddToCartClick}
            className="shop-product-card-btn-cart"
          >
            <ShoppingCart size={16} />
            <span>Add to Cart</span>
          </button>
          
          <span className="shop-product-card-btn-details">
            Details
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ShopProductCard;
